import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ArrowPathIcon,
    XMarkIcon,
    ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { axiosPrivate } from '../../../api/axios';
import { DrawerCustomClean } from '../../../components/drawer/DrawerCustomClean';
import './class-diagnostics.css';

/** Traducción de los eventos que registra liveClassLogger. */
const EVENT_LABELS = {
    'join:start': 'Intento de entrada',
    'join:ok': 'Entró a la videollamada',
    'connection-change': 'Cambio de conexión',
    'session-closed-unexpected': 'Se cayó la conexión',
    'reconnect-attempt': 'Intento de reconexión',
    'reconnect-ok': 'Reconectado',
    'reconnect-failed': 'Falló la reconexión',
    'reconnect-exhausted': 'Reconexión agotada',
    'network-quality-bad': 'Red inestable',
    'socket-join': 'Abrió la clase',
    'socket-leave': 'Salió de la clase',
    'socket-disconnect': 'Se cerró la pestaña o se perdió la red',
    'mute-all': 'Silenció a los estudiantes',
    'capture-recovered': 'Se recuperó la cámara',
};

const SEVERE_EVENTS = new Set([
    'session-closed-unexpected',
    'reconnect-failed',
    'reconnect-exhausted',
    'socket-disconnect',
]);

const eventLabel = (event) => EVENT_LABELS[event] || event;

const userName = (user) => {
    if (!user) return 'Usuario desconocido';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || user.email || 'Usuario desconocido';
};

const formatTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('es-MX', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
};

const StatCard = ({ label, value, tone = 'neutral' }) => (
    <div className={`cd-stat cd-stat--${tone}`}>
        <p className="cd-stat__value">{Number(value || 0).toLocaleString('es-MX')}</p>
        <p className="cd-stat__label">{label}</p>
    </div>
);

/**
 * Panel de diagnóstico para el director: quién se desconectó, cuántas veces y
 * si la reconexión automática funcionó.
 */
export const ClassDiagnosticsDrawer = ({ sessionId, title = '', isOpen, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        if (!sessionId) return;
        setLoading(true);
        setError('');
        try {
            const { data: payload } = await axiosPrivate.get(
                `/api/zoom/sessions/${sessionId}/connection-logs`,
            );
            setData(payload);
        } catch (err) {
            setError(
                err?.response?.data?.message
                || 'No se pudo cargar el diagnóstico de conexión',
            );
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    useEffect(() => {
        if (isOpen) void load();
    }, [isOpen, load]);

    const users = useMemo(() => {
        const rows = data?.users || [];
        return [...rows].sort((a, b) => (
            (b.disconnects - a.disconnects)
            || userName(a.user).localeCompare(userName(b.user), 'es')
        ));
    }, [data]);

    const logs = data?.logs || [];

    return (
        <DrawerCustomClean isOpen={isOpen} toggleDrawer={onClose} sizeLG="46%" sizeMD="80%">
            <div className="cd">
                <header className="cd__head">
                    <div className="cd__head-text">
                        <h2 className="cd__title">Diagnóstico de conexión</h2>
                        <p className="cd__subtitle">{title || 'Clase en vivo'}</p>
                    </div>
                    <div className="cd__head-actions">
                        <button
                            type="button"
                            className="cd__icon-btn"
                            title="Actualizar"
                            aria-label="Actualizar diagnóstico"
                            disabled={loading}
                            onClick={load}
                        >
                            <ArrowPathIcon aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            className="cd__icon-btn"
                            title="Cerrar"
                            aria-label="Cerrar diagnóstico"
                            onClick={onClose}
                        >
                            <XMarkIcon aria-hidden="true" />
                        </button>
                    </div>
                </header>

                {error ? (
                    <div className="cd__error" role="alert">
                        <ExclamationTriangleIcon aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                ) : null}

                {loading && !data ? (
                    <p className="cd__empty">Cargando eventos…</p>
                ) : null}

                {data ? (
                    <div className="cd__body">
                        <div className="cd__stats">
                            <StatCard label="Eventos" value={data.totals?.events} />
                            <StatCard label="Usuarios" value={data.totals?.users} />
                            <StatCard
                                label="Desconexiones"
                                value={data.totals?.disconnects}
                                tone={data.totals?.disconnects ? 'bad' : 'neutral'}
                            />
                            <StatCard
                                label="Reconexiones"
                                value={data.totals?.reconnects}
                                tone={data.totals?.reconnects ? 'good' : 'neutral'}
                            />
                        </div>

                        <section className="cd__section">
                            <h3 className="cd__section-title">Por usuario</h3>
                            {users.length === 0 ? (
                                <p className="cd__empty">
                                    Sin eventos registrados para esta clase.
                                </p>
                            ) : (
                                <div className="cd__table-wrap">
                                    <table className="cd__table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Usuario</th>
                                                <th scope="col">Caídas</th>
                                                <th scope="col">Reconexiones</th>
                                                <th scope="col">Fallas</th>
                                                <th scope="col">Último evento</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((row) => (
                                                <tr key={row.user?._id || userName(row.user)}>
                                                    <td>
                                                        <span className="cd__user">
                                                            {userName(row.user)}
                                                        </span>
                                                        {row.user?.typeUser ? (
                                                            <span className="cd__role">
                                                                {row.user.typeUser}
                                                            </span>
                                                        ) : null}
                                                    </td>
                                                    <td className={row.disconnects ? 'is-bad' : ''}>
                                                        {row.disconnects}
                                                    </td>
                                                    <td className={row.reconnects ? 'is-good' : ''}>
                                                        {row.reconnects}
                                                    </td>
                                                    <td className={row.failures ? 'is-bad' : ''}>
                                                        {row.failures}
                                                    </td>
                                                    <td>
                                                        <span className="cd__last">
                                                            {eventLabel(row.lastEvent)}
                                                        </span>
                                                        <span className="cd__at">
                                                            {formatTime(row.lastAt)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        <section className="cd__section">
                            <h3 className="cd__section-title">
                                Bitácora
                                <span className="cd__section-count">{logs.length}</span>
                            </h3>
                            {logs.length === 0 ? (
                                <p className="cd__empty">Nada que mostrar todavía.</p>
                            ) : (
                                <ul className="cd__log">
                                    {logs.map((log) => (
                                        <li
                                            key={log._id}
                                            className={SEVERE_EVENTS.has(log.event) ? 'is-severe' : ''}
                                        >
                                            <span className="cd__log-at">{formatTime(log.at)}</span>
                                            <span className="cd__log-event">
                                                {eventLabel(log.event)}
                                            </span>
                                            <span className="cd__log-user">
                                                {userName(log.user)}
                                            </span>
                                            {log.reason ? (
                                                <span className="cd__log-reason">{log.reason}</span>
                                            ) : null}
                                            {log.attempt ? (
                                                <span className="cd__log-badge">
                                                    intento {log.attempt}
                                                </span>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>
                ) : null}
            </div>
        </DrawerCustomClean>
    );
};
